/**
 * Weight conversion utilities for EasyPost integration
 * EasyPost requires weights in ounces for customs declarations
 */

export type WeightUnit = 'g' | 'kg' | 'oz' | 'lb' | 'ml';

/**
 * Conversion factors to ounces
 * Note: ml is included for liquid products, using water density (1ml ≈ 1g)
 */
const CONVERSION_TO_OUNCES = {
  g: 0.035274,     // 1 gram = 0.035274 ounces
  kg: 35.274,      // 1 kilogram = 35.274 ounces
  oz: 1,           // 1 ounce = 1 ounce
  lb: 16,          // 1 pound = 16 ounces
  ml: 0.035274,    // 1 ml ≈ 1g = 0.035274 ounces (using water density)
} as const;

/**
 * Convert weight from any supported unit to ounces
 */
export const convertToOunces = (weight: number, unit: WeightUnit): number => {
  if (weight <= 0) {
    return 0;
  }

  const conversionFactor = CONVERSION_TO_OUNCES[unit];
  if (!conversionFactor) {
    throw new Error(`Unsupported weight unit: ${unit}`);
  }

  return Number((weight * conversionFactor).toFixed(4));
};

/**
 * Convert weight from ounces to any supported unit
 */
export const convertFromOunces = (weightInOunces: number, targetUnit: WeightUnit): number => {
  if (weightInOunces <= 0) {
    return 0;
  }

  const conversionFactor = CONVERSION_TO_OUNCES[targetUnit];
  if (!conversionFactor) {
    throw new Error(`Unsupported weight unit: ${targetUnit}`);
  }

  return Number((weightInOunces / conversionFactor).toFixed(4));
};

/**
 * Format weight with unit for display
 */
export const formatWeight = (weight: number, unit: WeightUnit): string => {
  if (weight <= 0) {
    return `0 ${unit}`;
  }

  // Round to reasonable decimal places
  const roundedWeight = unit === 'g' || unit === 'ml' 
    ? Math.round(weight) 
    : Number(weight.toFixed(2));

  return `${roundedWeight} ${unit}`;
};

/**
 * Validate weight and unit combination
 */
export const validateWeight = (weight: number, unit: WeightUnit): string[] => {
  const errors: string[] = [];

  if (weight <= 0) {
    errors.push('Weight must be greater than 0');
  }

  if (!CONVERSION_TO_OUNCES[unit]) {
    errors.push(`Invalid weight unit: ${unit}`);
  }

  // Check for reasonable limits
  const weightInOunces = convertToOunces(weight, unit);
  
  // Maximum reasonable package weight (150 lbs = 2400 oz)
  if (weightInOunces > 2400) {
    errors.push('Weight is too large for shipping (max 150 lbs)');
  }

  // Minimum reasonable weight (0.01 oz)
  if (weightInOunces < 0.01) {
    errors.push('Weight is too small (minimum 0.01 oz)');
  }

  return errors;
};

/**
 * Calculate total weight of multiple items in ounces
 */
export const calculateTotalWeightInOunces = (
  items: Array<{ weight: number; weightUnit: WeightUnit; quantity?: number }>
): number => {
  return items.reduce((total, item) => {
    const quantity = item.quantity || 1;
    const weightInOunces = convertToOunces(item.weight, item.weightUnit);
    return total + (weightInOunces * quantity);
  }, 0);
};

/**
 * Get suggested weight unit based on weight value
 */
export const getSuggestedUnit = (weight: number, currentUnit: WeightUnit): WeightUnit => {
  const weightInOunces = convertToOunces(weight, currentUnit);

  // Very light items (< 1 oz) - suggest grams
  if (weightInOunces < 1) {
    return 'g';
  }

  // Light items (< 16 oz) - suggest ounces
  if (weightInOunces < 16) {
    return 'oz';
  }

  // Heavy items (>= 16 oz) - suggest pounds
  return 'lb';
};

/**
 * Convert product weight for EasyPost customs declaration
 */
export const convertProductWeightForCustoms = (
  weight: number | undefined | null,
  weightUnit: WeightUnit | undefined | null,
  quantity: number = 1
): number => {
  // Default weight if not specified (assuming very light item)
  if (!weight || weight <= 0) {
    return 0.1 * quantity; // 0.1 oz per item as default
  }

  // Default unit if not specified
  const unit = weightUnit || 'oz';

  const weightInOunces = convertToOunces(weight, unit);
  return Number((weightInOunces * quantity).toFixed(4));
};